import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Calendar, User, Clock, MapPin, Video, Building, X, Check, Search, Key } from 'lucide-react';
import { format, parseISO, isPast, isToday } from 'date-fns';
import { fr } from 'date-fns/locale';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ClientDashboard = () => {
  const navigate = useNavigate();
  const { user, token, isAuthenticated, isClient, updateUser } = useAuth();
  
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('upcoming');
  const [activeVideoCall, setActiveVideoCall] = useState(null);
  
  const [profileForm, setProfileForm] = useState({
    first_name: '',
    last_name: '',
    phone: ''
  });
  const [saving, setSaving] = useState(false);

  // Password change
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [changingPassword, setChangingPassword] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/auth');
      return;
    }
    if (!isClient) {
      navigate('/practitioner/dashboard');
      return;
    }

    const fetchData = async () => {
      try {
        const response = await axios.get(`${API}/appointments/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setAppointments(response.data);
      } catch (error) {
        console.error('Error fetching appointments:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [isAuthenticated, isClient, navigate, token]);

  useEffect(() => {
    if (user) {
      setProfileForm({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        phone: user.phone || ''
      });
    }
  }, [user]);

  const cancelAppointment = async (appointmentId) => {
    try {
      await axios.put(
        `${API}/appointments/${appointmentId}/status?status=cancelled`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAppointments(prev => prev.map(apt => 
        apt.id === appointmentId ? { ...apt, status: 'cancelled' } : apt
      ));
      toast.success('Rendez-vous annulé');
    } catch (error) {
      toast.error('Erreur lors de l\'annulation');
    }
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await updateUser(profileForm);
      toast.success('Profil mis à jour');
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }
    if (passwordForm.new_password.length < 6) {
      toast.error('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }
    
    setChangingPassword(true);
    try {
      await axios.post(
        `${API}/auth/change-password`,
        {
          current_password: passwordForm.current_password,
          new_password: passwordForm.new_password
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Mot de passe modifié avec succès');
      setPasswordModalOpen(false);
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du changement de mot de passe');
    } finally {
      setChangingPassword(false);
    }
  };

  // Filter appointments
  const upcomingAppointments = appointments.filter(apt => {
    const aptDate = parseISO(apt.date);
    return !isPast(aptDate) || isToday(aptDate);
  }).filter(apt => apt.status !== 'cancelled');

  const pastAppointments = appointments.filter(apt => {
    const aptDate = parseISO(apt.date);
    return isPast(aptDate) && !isToday(aptDate);
  });

  const cancelledAppointments = appointments.filter(apt => apt.status === 'cancelled');

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <span className="badge-status badge-pending">En attente</span>;
      case 'confirmed':
        return <span className="badge-status badge-confirmed">Confirmé</span>;
      case 'cancelled':
        return <span className="badge-status badge-cancelled">Annulé</span>;
      case 'completed':
        return <span className="badge-status badge-completed">Terminé</span>;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex items-center justify-center py-20">
          <div className="loader" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="client-dashboard">
      <Navbar />
      
      <div className="container-app py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-heading text-3xl font-semibold text-foreground mb-2">
            Bonjour, {user?.first_name} !
          </h1>
          <p className="text-muted-foreground">
            Gérez vos rendez-vous et votre profil
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="grid w-full grid-cols-3 mb-6 bg-sand-100 p-1 rounded-xl">
                <TabsTrigger 
                  value="upcoming" 
                  className="rounded-lg data-[state=active]:bg-white"
                  data-testid="tab-upcoming"
                >
                  À venir ({upcomingAppointments.length})
                </TabsTrigger>
                <TabsTrigger 
                  value="past" 
                  className="rounded-lg data-[state=active]:bg-white"
                  data-testid="tab-past"
                >
                  Passés ({pastAppointments.length})
                </TabsTrigger>
                <TabsTrigger 
                  value="cancelled" 
                  className="rounded-lg data-[state=active]:bg-white"
                  data-testid="tab-cancelled"
                >
                  Annulés ({cancelledAppointments.length})
                </TabsTrigger>
              </TabsList>

              {/* Upcoming Appointments */}
              <TabsContent value="upcoming">
                {upcomingAppointments.length === 0 ? (
                  <div className="empty-state bg-white rounded-2xl border border-sand-200">
                    <Calendar className="w-12 h-12 mx-auto mb-4 text-muted-foreground/50" />
                    <h3 className="font-heading text-xl font-medium mb-2">Aucun rendez-vous à venir</h3>
                    <p className="text-muted-foreground mb-4">
                      Trouvez un praticien et réservez votre première consultation
                    </p>
                    <Link to="/search">
                      <Button className="bg-sage-500 hover:bg-sage-600" data-testid="search-practitioners-btn">
                        <Search className="w-4 h-4 mr-2" />
                        Rechercher un praticien
                      </Button>
                    </Link>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {upcomingAppointments.map((apt) => (
                      <div 
                        key={apt.id}
                        className="bg-white rounded-2xl border border-sand-200 shadow-soft p-5"
                        data-testid={`appointment-${apt.id}`}
                      >
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                          <div className="flex items-start gap-4">
                            <div className="w-14 h-14 bg-sage-100 rounded-xl flex items-center justify-center shrink-0">
                              <Calendar className="w-6 h-6 text-sage-600" />
                            </div>
                            <div>
                              <div className="flex items-center gap-2 mb-1">
                                <h3 className="font-medium text-foreground">
                                  {apt.practitioner?.user?.first_name} {apt.practitioner?.user?.last_name}
                                </h3>
                                {getStatusBadge(apt.status)}
                              </div>
                              <p className="text-sm text-muted-foreground capitalize mb-2">
                                {apt.practitioner?.specialty}
                              </p>
                              <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                                <span className="flex items-center gap-1">
                                  <Calendar className="w-4 h-4" />
                                  {format(parseISO(apt.date), 'EEEE d MMMM', { locale: fr })}
                                </span>
                                <span className="flex items-center gap-1">
                                  <Clock className="w-4 h-4" />
                                  {apt.start_time}
                                </span>
                                <span className="flex items-center gap-1">
                                  {apt.consultation_type === 'cabinet' ? (
                                    <><Building className="w-4 h-4" /> Cabinet</>
                                  ) : (
                                    <><Video className="w-4 h-4" /> Visio</>
                                  )}
                                </span>
                              </div>
                              {/* Video Link for visio appointments */}
                              {apt.consultation_type === 'visio' && apt.video_room_url && apt.status === 'confirmed' && (
                                <div className="mt-3">
                                  <button 
                                    onClick={() => {
                                      // Extract room ID from URL and navigate to video page as client
                                      const roomId = apt.video_room_url.split('/').pop();
                                      navigate(`/video/${roomId}?name=${encodeURIComponent(user?.first_name || 'Client')}&role=client`);
                                    }}
                                    className="inline-flex items-center gap-2 px-4 py-2 bg-terracotta-500 text-white rounded-lg text-sm font-medium hover:bg-terracotta-600 transition-colors"
                                    data-testid={`join-video-${apt.id}`}
                                  >
                                    <Video className="w-4 h-4" />
                                    Rejoindre la consultation vidéo
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                          
                          <div className="flex gap-2 md:shrink-0">
                            <Link to={`/practitioner/${apt.practitioner_id}`}>
                              <Button variant="outline" size="sm" data-testid={`view-practitioner-${apt.id}`}>
                                Voir le profil
                              </Button>
                            </Link>
                            {apt.status !== 'cancelled' && (
                              <Button 
                                variant="ghost" 
                                size="sm"
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                onClick={() => cancelAppointment(apt.id)}
                                data-testid={`cancel-appointment-${apt.id}`}
                              >
                                <X className="w-4 h-4 mr-1" />
                                Annuler
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>

              {/* Past Appointments */}
              <TabsContent value="past">
                {pastAppointments.length === 0 ? (
                  <div className="empty-state bg-white rounded-2xl border border-sand-200">
                    <Clock className="w-12 h-12 mx-auto mb-4 text-muted-foreground/50" />
                    <h3 className="font-heading text-xl font-medium mb-2">Aucun rendez-vous passé</h3>
                    <p className="text-muted-foreground">
                      Vos consultations passées apparaîtront ici
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {pastAppointments.map((apt) => (
                      <div 
                        key={apt.id}
                        className="bg-white rounded-2xl border border-sand-200 shadow-soft p-5 opacity-75"
                        data-testid={`past-appointment-${apt.id}`}
                      >
                        <div className="flex items-start gap-4">
                          <div className="w-14 h-14 bg-sand-100 rounded-xl flex items-center justify-center shrink-0">
                            <Check className="w-6 h-6 text-muted-foreground" />
                          </div>
                          <div>
                            <h3 className="font-medium text-foreground mb-1">
                              {apt.practitioner?.user?.first_name} {apt.practitioner?.user?.last_name}
                            </h3>
                            <p className="text-sm text-muted-foreground mb-2">
                              {format(parseISO(apt.date), 'EEEE d MMMM yyyy', { locale: fr })} à {apt.start_time}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>

              {/* Cancelled Appointments */}
              <TabsContent value="cancelled">
                {cancelledAppointments.length === 0 ? (
                  <div className="empty-state bg-white rounded-2xl border border-sand-200">
                    <X className="w-12 h-12 mx-auto mb-4 text-muted-foreground/50" />
                    <h3 className="font-heading text-xl font-medium mb-2">Aucun rendez-vous annulé</h3>
                    <p className="text-muted-foreground">
                      Vos annulations apparaîtront ici
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {cancelledAppointments.map((apt) => (
                      <div 
                        key={apt.id}
                        className="bg-white rounded-2xl border border-sand-200 shadow-soft p-5 opacity-60"
                        data-testid={`cancelled-appointment-${apt.id}`}
                      >
                        <div className="flex items-start gap-4">
                          <div className="w-14 h-14 bg-red-50 rounded-xl flex items-center justify-center shrink-0">
                            <X className="w-6 h-6 text-red-400" />
                          </div>
                          <div>
                            <h3 className="font-medium text-foreground mb-1">
                              {apt.practitioner?.user?.first_name} {apt.practitioner?.user?.last_name}
                            </h3>
                            <p className="text-sm text-muted-foreground">
                              {format(parseISO(apt.date), 'EEEE d MMMM yyyy', { locale: fr })} à {apt.start_time}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </div>

          {/* Sidebar - Profile */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl border border-sand-200 shadow-soft p-6" data-testid="profile-section">
              <h2 className="font-heading text-lg font-medium mb-6">Mon profil</h2>
              
              <form onSubmit={handleProfileUpdate} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="profile-firstname">Prénom</Label>
                  <Input
                    id="profile-firstname"
                    value={profileForm.first_name}
                    onChange={(e) => setProfileForm({ ...profileForm, first_name: e.target.value })}
                    className="rounded-xl border-sand-200"
                    data-testid="profile-firstname"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="profile-lastname">Nom</Label>
                  <Input
                    id="profile-lastname"
                    value={profileForm.last_name}
                    onChange={(e) => setProfileForm({ ...profileForm, last_name: e.target.value })}
                    className="rounded-xl border-sand-200"
                    data-testid="profile-lastname"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="profile-phone">Téléphone</Label>
                  <Input
                    id="profile-phone"
                    value={profileForm.phone}
                    onChange={(e) => setProfileForm({ ...profileForm, phone: e.target.value })}
                    className="rounded-xl border-sand-200"
                    data-testid="profile-phone"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input
                    value={user?.email || ''}
                    disabled
                    className="rounded-xl border-sand-200 bg-sand-50"
                  />
                </div>

                <Button 
                  type="submit" 
                  className="w-full bg-sage-500 hover:bg-sage-600 rounded-xl"
                  disabled={saving}
                  data-testid="save-profile-btn"
                >
                  {saving ? 'Enregistrement...' : 'Enregistrer'}
                </Button>

                <Button 
                  type="button"
                  variant="outline"
                  onClick={() => setPasswordModalOpen(true)}
                  className="w-full rounded-xl mt-3"
                  data-testid="change-password-btn"
                >
                  <Key className="w-4 h-4 mr-2" />
                  Changer mon mot de passe
                </Button>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Change Password Modal */}
      <Dialog open={passwordModalOpen} onOpenChange={setPasswordModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="w-5 h-5 text-sage-600" />
              Changer mon mot de passe
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleChangePassword} className="space-y-4">
            <div className="space-y-2">
              <Label>Mot de passe actuel</Label>
              <Input
                type="password"
                value={passwordForm.current_password}
                onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })}
                className="rounded-xl border-sand-200"
                required
                data-testid="current-password-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Nouveau mot de passe</Label>
              <Input
                type="password"
                value={passwordForm.new_password}
                onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
                placeholder="Minimum 6 caractères"
                className="rounded-xl border-sand-200"
                required
                data-testid="new-password-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Confirmer le nouveau mot de passe</Label>
              <Input
                type="password"
                value={passwordForm.confirm_password}
                onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })}
                className="rounded-xl border-sand-200"
                required
                data-testid="confirm-password-input"
              />
            </div>
            <div className="flex gap-3 justify-end">
              <Button 
                type="button"
                variant="outline" 
                onClick={() => setPasswordModalOpen(false)}
                className="rounded-xl"
              >
                Annuler
              </Button>
              <Button 
                type="submit"
                disabled={changingPassword}
                className="bg-sage-500 hover:bg-sage-600 rounded-xl"
                data-testid="submit-change-password"
              >
                {changingPassword ? 'Modification...' : 'Modifier'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClientDashboard;
